/**
 * Canonical /inquire link + context helpers.
 *
 * Every CTA that routes into /inquire should build its href with
 * `buildInquireHref()` so the same three attribution dimensions travel with
 * the visitor end-to-end:
 *
 *   lane   — which service lane the CTA belongs to (advocacy, coaching, …)
 *   tier   — the retainer/pricing tier selected, when there is one
 *   source — the surface/placement the click came from (quickpick, sticky, …)
 *
 * On top of that, any UTM / click-id present on the current URL (or captured
 * earlier this session via `@/lib/utm`) is forwarded onto the /inquire URL so
 * campaign attribution is not lost on internal navigation. /inquire then reads
 * everything back with `readInquireContext()` and stamps it onto the form-view,
 * submit, and confirmation analytics events.
 */
import { UTM_KEYS, getAttribution } from "@/lib/utm";

const CLICK_ID_KEYS = ["gclid", "fbclid", "msclkid", "ttclid", "li_fat_id"] as const;
const FORWARD_KEYS = [...UTM_KEYS, ...CLICK_ID_KEYS] as const;

export interface InquireLinkParams {
  lane?: string;
  tier?: string;
  source?: string;
  intent?: string;
  /** Any extra prefill params (name, email, organization, role, …). */
  extra?: Record<string, string | number | undefined | null>;
  /** Base path — defaults to `/inquire`. */
  path?: string;
}

const clean = (v: unknown, max = 120) =>
  typeof v === "string" || typeof v === "number"
    ? String(v).trim().slice(0, max)
    : "";

const slug = (v: unknown) => clean(v).toLowerCase().replace(/\s+/g, "-");

/** Campaign params to carry forward: current URL first, stored attribution second. */
export function forwardedCampaignParams(
  search = typeof window === "undefined" ? "" : window.location.search,
): Record<string, string> {
  const out: Record<string, string> = {};
  const current = new URLSearchParams(search);
  for (const key of FORWARD_KEYS) {
    const v = current.get(key);
    if (v) out[key] = v.slice(0, 200);
  }
  const stored = getAttribution() as Record<string, string | number | undefined>;
  for (const key of FORWARD_KEYS) {
    if (!out[key] && stored?.[key]) out[key] = String(stored[key]).slice(0, 200);
  }
  return out;
}

/** Build an /inquire href carrying lane, tier, source, intent, and campaign data. */
export function buildInquireHref({
  lane,
  tier,
  source,
  intent,
  extra,
  path = "/inquire",
}: InquireLinkParams = {}): string {
  const params = new URLSearchParams();
  const push = (k: string, v: string) => {
    if (v) params.set(k, v);
  };

  push("lane", slug(lane));
  push("tier", slug(tier));
  push("source", slug(source));
  push("intent", slug(intent));

  for (const [k, v] of Object.entries(extra ?? {})) {
    push(k, clean(v, 500));
  }
  for (const [k, v] of Object.entries(forwardedCampaignParams())) {
    push(k, v);
  }

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export interface InquireContext {
  lane: string;
  tier: string;
  source: string;
  intent: string;
  /** Flat utm_* / click-id props, ready to spread into an analytics payload. */
  campaign: Record<string, string>;
}

/** Read the propagated context back out of an /inquire URL. */
export function readInquireContext(params: URLSearchParams): InquireContext {
  const campaign: Record<string, string> = {};
  for (const key of FORWARD_KEYS) {
    const v = params.get(key);
    if (v) campaign[key] = v.slice(0, 200);
  }
  // Fall back to session attribution when the link carried no campaign data.
  if (Object.keys(campaign).length === 0) {
    Object.assign(campaign, forwardedCampaignParams(""));
  }
  return {
    lane: slug(params.get("lane")),
    tier: slug(params.get("tier")),
    source: slug(params.get("source")),
    intent: slug(params.get("intent")),
    campaign,
  };
}

/** Analytics-ready props for a given context (used on view/submit/confirm). */
export function inquireContextProps(
  ctx: InquireContext,
): Record<string, string | boolean> {
  return {
    inquire_lane: ctx.lane || "unset",
    inquire_tier: ctx.tier || "unset",
    inquire_source: ctx.source || "direct",
    inquire_intent: ctx.intent || "none",
    has_campaign: Object.keys(ctx.campaign).length > 0,
    ...ctx.campaign,
  };
}
